import { ArrayUtils } from '@shopping/common'
import { SubVertical, isUsing1stMenuInsteadOfRootId, isUsingCompositeMenuBySubVertical } from 'common/constants'
import { MenuType } from 'common/constants/menu'
import { StateKey } from 'common/utils/flipState'
import { DisplayMenu, MenuLayer, ROOT_ID } from 'common/types/entities/mongo/menu'
import {
  fetchBy,
  fetchLayers,
  fetchLayersOfBranch,
  fetchLayersOfChannel,
  fetchMenusOfBranch,
  fetchMenusOfChannel,
  fetchWithFilter,
} from 'common/services/finder/menu'

export enum DisplayType {
  NO_RULE = 'NO_RULE',
  CATEGORY_HOME = 'CATEGORY_HOME',
  COMPOSITE_CATEGORY_HOME = 'COMPOSITE_CATEGORY_HOME',
  VERTICAL_HOME = 'VERTICAL_HOME',
  STORE_HOME = 'STORE_HOME',
  COMPOSITE_STORE_HOME = 'COMPOSITE_STORE_HOME',
  BRANCH_HOME = 'BRANCH_HOME',
}

export interface MenuState {
  subVertical: SubVertical
  channelId: string
  storeCategoryId: string
  displayType: DisplayType
  first: MenuLayer
  second: MenuLayer
  third: MenuLayer
}

const initialState: MenuState = {
  subVertical: null,
  channelId: null,
  storeCategoryId: null,
  displayType: DisplayType.CATEGORY_HOME,
  first: {},
  second: {},
  third: {},
}

// action types
const INIT = 'menu/INIT'
const SET_FIRST = 'menu/SET_FIRST'
const SET_SECOND = 'menu/SET_SECOND'
const SET_THIRD = 'menu/SET_THIRD'

export const initHome = (curState: StateKey, subVertical: SubVertical, paramMenuId?: string) => async dispatch => {
  const displayMenus: DisplayMenu[] = await fetchBy(subVertical, ROOT_ID, DisplayType.VERTICAL_HOME)
  dispatch({
    curState,
    type: INIT,
    result: {
      subVertical,
      first: generateMenuLayer(displayMenus, paramMenuId || ROOT_ID),
      displayType: DisplayType.VERTICAL_HOME,
    },
  })
}

export const initMenu = (
  curState: StateKey,
  subVertical: SubVertical,
  paramMenuId?: string,
  menuTypes?: MenuType[],
) => async dispatch => {
  if (isValidMenuId(paramMenuId)) {
    const layers: MenuLayer[] = await fetchLayers(paramMenuId, DisplayType.CATEGORY_HOME)
    dispatch({
      curState,
      type: INIT,
      result: {
        subVertical,
        first: (layers && layers[0]) || {},
        second: (layers && layers[1]) || {},
        third: (layers && layers[2]) || {},
        displayType: DisplayType.CATEGORY_HOME,
      },
    })
  } else {
    const displayMenus: DisplayMenu[] = await fetchBy(subVertical, ROOT_ID, DisplayType.CATEGORY_HOME, menuTypes)
    if (isUsing1stMenuInsteadOfRootId(subVertical)) {
      const first = ArrayUtils.getFirst(displayMenus)
      await initMenu(curState, subVertical, first.id, menuTypes)(dispatch)
      return
    }
    dispatch({
      curState,
      type: INIT,
      result: {
        subVertical,
        first: generateMenuLayer(displayMenus, ROOT_ID),
        displayType: DisplayType.CATEGORY_HOME,
      },
    })
  }
}

const isValidMenuId = (menuId: string) => menuId && menuId !== ROOT_ID

const generateMenuLayer = (displayMenus: DisplayMenu[], selectedId: string) => ({ displayMenus, selectedId })

export const initMenuOfStore = (
  curState: StateKey,
  subVertical: SubVertical,
  channelId: string,
  paramMenuId?: string,
) => async dispatch => {
  if (isValidMenuId(paramMenuId)) {
    const disPlayType: DisplayType = isUsingCompositeMenuBySubVertical(subVertical)
      ? DisplayType.COMPOSITE_STORE_HOME
      : DisplayType.STORE_HOME
    const layers: MenuLayer[] = await fetchLayersOfChannel(channelId, paramMenuId, disPlayType, subVertical)
    dispatch({
      curState,
      type: INIT,
      result: {
        subVertical,
        channelId,
        first: layers[0] || {},
        second: layers[1] || {},
        third: layers[2] || {},
        displayType: DisplayType.STORE_HOME,
      },
    })
  } else {
    const displayMenus: DisplayMenu[] = await fetchMenusOfChannel(
      channelId,
      ROOT_ID,
      DisplayType.STORE_HOME,
      subVertical,
    )
    dispatch({
      curState,
      type: INIT,
      result: {
        subVertical,
        channelId,
        first: generateMenuLayer(displayMenus, ROOT_ID),
        displayType: DisplayType.STORE_HOME,
      },
    })
  }
}

export const initMenuOfBranch = (
  curState: StateKey,
  subVertical: SubVertical,
  storeCategoryId: string,
  paramMenuId?: string,
) => async dispatch => {
  if (isValidMenuId(paramMenuId)) {
    const layers: MenuLayer[] = await fetchLayersOfBranch(storeCategoryId, paramMenuId)
    dispatch({
      curState,
      type: INIT,
      result: {
        subVertical,
        storeCategoryId,
        first: layers[0] || {},
        second: layers[1] || {},
        third: layers[2] || {},
        displayType: DisplayType.BRANCH_HOME,
      },
    })
  } else {
    const displayMenus: DisplayMenu[] = await fetchMenusOfBranch(storeCategoryId)
    dispatch({
      curState,
      type: INIT,
      result: {
        subVertical,
        storeCategoryId,
        first: generateMenuLayer(displayMenus, ROOT_ID),
        displayType: DisplayType.BRANCH_HOME,
      },
    })
  }
}

export const initMenuWithFilter = (
  curState: StateKey,
  subVertical: SubVertical,
  paramMenuId?: string,
) => async dispatch => {
  const layers: MenuLayer[] = await fetchWithFilter(subVertical, paramMenuId, DisplayType.VERTICAL_HOME)
  dispatch({
    curState,
    type: INIT,
    result: {
      subVertical,
      first: layers[0] || {},
      second: layers[1] || {},
      third: layers[2] || {},
      displayType: DisplayType.VERTICAL_HOME,
    },
  })
}

export const setFirstMenu = (curState: StateKey, menuId: string) => async (dispatch, getState) => {
  const state: MenuState = getState().menu[curState]
  const displayMenus: DisplayMenu[] =
    menuId !== state.first.selectedId ? await fetchChildMenus(menuId, ROOT_ID, state) : state.second.displayMenus
  dispatch({ curState, menuId, second: generateMenuLayer(displayMenus, menuId), type: SET_FIRST })
}

const fetchChildMenus = async (menuId: string, selectedIdOnUpperDepth: string, state: MenuState) => {
  if (menuId === selectedIdOnUpperDepth) {
    return []
  }

  if (state.displayType === DisplayType.VERTICAL_HOME) {
    return []
  }

  if (state.displayType === DisplayType.STORE_HOME) {
    return await fetchMenusOfChannel(state.channelId, menuId, DisplayType.STORE_HOME)
  }

  if (state.displayType === DisplayType.BRANCH_HOME) {
    return await fetchMenusOfBranch(state.storeCategoryId, menuId)
  }

  return await fetchBy(state.subVertical, menuId, DisplayType.CATEGORY_HOME)
}

export const setSecondMenu = (curState: StateKey, menuId: string) => async (dispatch, getState) => {
  const state: MenuState = getState().menu[curState]
  const displayMenus: DisplayMenu[] =
    menuId !== state.second.selectedId
      ? await fetchChildMenus(menuId, state.first.selectedId, state)
      : state.third.displayMenus
  dispatch({ curState, menuId, third: generateMenuLayer(displayMenus, menuId), type: SET_SECOND })
}

export const setThirdMenu = (curState: StateKey, menuId: string) => dispatch => {
  dispatch({ curState, menuId, type: SET_THIRD })
}

// reducer
export default (state = initialState, action) => {
  switch (action.type) {
    case INIT:
      return { ...initialState, ...action.result }
    case SET_FIRST:
      return { ...state, first: { ...state.first, selectedId: action.menuId }, second: action.second, third: {} }
    case SET_SECOND:
      return { ...state, second: { ...state.second, selectedId: action.menuId }, third: action.third }
    case SET_THIRD:
      return { ...state, third: { ...state.third, selectedId: action.menuId } }
    default:
      return state
  }
}
